<template>
    <v-card variant="text" class="pa-2">
        <v-card-title class="text-wrap" style="word-break: break-word">
            {{ pregunta.texto }}
        </v-card-title>
        <template v-if="pregunta.imagen && pregunta.hotspot_objetivo">
            <v-card-subtitle class="text-wrap py-2">
                {{ t('Hotspot_senala_en_la_imagen') }} <strong>{{ pregunta.hotspot_objetivo.texto }}</strong>
            </v-card-subtitle>
            <div class="hotspot-imagen">
                <img :src="pregunta.imagen" class="w-100 hotspot-img"
                    :alt="t('Hotspot_imagen_de_la_pregunta')"
                    draggable="false" @click="marcarPunto" />
                <svg v-if="punto || regionCorrecta" class="hotspot-marcador"
                    viewBox="0 0 1000 1000" preserveAspectRatio="none"
                    aria-hidden="true">
                    <!-- Se revela únicamente tras el envío y confirmación del servidor. -->
                    <polygon v-if="regionPuntos" :points="regionPuntos"
                        fill="rgba(76, 175, 80, 0.28)" stroke="#2e7d32"
                        stroke-width="3" vector-effect="non-scaling-stroke" />
                    <circle v-if="punto" :cx="punto.x" :cy="punto.y" r="9"
                        fill="#e53935" stroke="#fff" stroke-width="3"
                        vector-effect="non-scaling-stroke" />
                </svg>
            </div>
            <p v-if="!haRespondido" class="text-caption mt-2">
                {{ t('Hotspot_pulsa_y_confirma') }}
            </p>
            <p v-else-if="regionPuntos" class="text-caption text-success mt-2">
                {{ t('Hotspot_zona_correcta') }}
            </p>
            <v-alert v-if="error" class="my-2" type="error" variant="tonal">
                {{ error }}
            </v-alert>
            <v-alert v-if="haRespondido" class="my-2"
                :type="acierto ? 'success' : 'error'" variant="tonal">
                {{ acierto ? t('Hotspot_correcto') : t('Hotspot_incorrecto') }}
            </v-alert>
            <v-btn v-if="!haRespondido" block color="primary" class="mt-3"
                :disabled="!punto || loading" :loading="loading" @click="enviarRespuesta">
                {{ t('Hotspot_confirmar_respuesta') }}
            </v-btn>
            <v-btn v-else block color="primary" class="mt-3" @click="continuar">
                {{ t('Hotspot_continuar') }}
            </v-btn>
        </template>
        <v-alert v-else type="warning" variant="tonal" class="ma-2">
            {{ t('Hotspot_sin_imagen_o_region') }}
        </v-alert>
    </v-card>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';

const props = defineProps({
    pregunta: { type: Object, required: true },
    selectedOptionVista: { type: String, default: 'alumno' },
    alumnoID: { type: [Number, String], default: null },
});
const emit = defineEmits(['respuesta-enviada']);
const { t } = useI18n();
const punto = ref(null);
const regionCorrecta = ref(null);
const regionPuntos = computed(() => {
    const anillo = regionCorrecta.value?.coordinates?.[0];
    return Array.isArray(anillo)
        ? anillo.map(([x, y]) => `${x},${y}`).join(' ')
        : '';
});
const loading = ref(false);
const haRespondido = ref(false);
const acierto = ref(false);
const error = ref('');

function marcarPunto(event) {
    if (haRespondido.value || loading.value) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    punto.value = {
        x: Math.max(0, Math.min(1000, Math.round(
            (event.clientX - rect.left) / rect.width * 1000
        ))),
        y: Math.max(0, Math.min(1000, Math.round(
            (event.clientY - rect.top) / rect.height * 1000
        ))),
    };
    error.value = '';
}

async function enviarRespuesta() {
    if (!punto.value || !props.pregunta.hotspot_objetivo || loading.value) return;
    loading.value = true;
    error.value = '';
    try {
        const { data } = await axios.post('enviar_respuestas/', {
            pregunta_id: props.pregunta.id,
            objetivo_token: props.pregunta.hotspot_objetivo.token,
            click_x: punto.value.x,
            click_y: punto.value.y,
            selectedOptionVista: props.selectedOptionVista,
            alumnoID: props.alumnoID,
        });
        if (!data.success) throw new Error(data.message || t('Hotspot_error_guardar_respuesta'));
        acierto.value = Boolean(data.acierto);
        // No se dibuja nada hasta que el backend confirme el guardado.
        regionCorrecta.value = data.region_correcta?.type === 'Polygon'
            ? data.region_correcta : null;
        haRespondido.value = true;
    } catch (err) {
        console.error('Hotspot: no se pudo guardar la respuesta', err);
        error.value = t('Hotspot_error_guardar_respuesta');
    } finally {
        loading.value = false;
    }
}

function continuar() {
    if (haRespondido.value) emit('respuesta-enviada', props.pregunta);
}

watch(() => props.pregunta.hotspot_objetivo?.token, () => {
    punto.value = null;
    regionCorrecta.value = null;
    haRespondido.value = false;
    acierto.value = false;
    error.value = '';
});
</script>

<style scoped>
.hotspot-imagen {
    position: relative;
    width: 100%;
    max-width: 850px;
    margin: 0 auto;
    line-height: 0;
}
.hotspot-img {
    display: block;
    height: auto;
    cursor: crosshair;
    user-select: none;
}
.hotspot-marcador {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}
</style>
